import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AssignmentRole, ProjectAccess, ProjectAccessAssignmentMask, ProjectAccessRoles } from '@completion/models';
import { getAssignmentRoles, getCurrentProject, getProjectAccessList, getProjectAccessMasks } from '@completion/selectors';
import { ProjectAccessListLoad, AssignmentRoleLoad, ProjectAccessMasksLoad, UpdateProjectAccessMask } from '@completion/actions';

interface AssignmentRoleMatrixVm {
  roles: AssignmentRole[];
  projectAccessList: ProjectAccess[];
  masks: ProjectAccessAssignmentMask[];
  displayedColumns: string[];
}

@Component({
  selector: 'app-assignment-role-matrix',
  templateUrl: './assignment-role-matrix.component.html',
  styleUrls: ['./assignment-role-matrix.component.scss'],
  standalone: false,
})
export class AssignmentRoleMatrixComponent implements OnInit, OnDestroy {
  // Base streams from store
  private projectAccessList$: Observable<ProjectAccess[]>;
  private roles$: Observable<AssignmentRole[]>;
  private masks$: Observable<ProjectAccessAssignmentMask[]>;

  // Filter streams
  private readonly userFilter$ = new BehaviorSubject<string>('');
  private readonly roleFilter$ = new BehaviorSubject<string>('');

  // Public view model that the template binds to
  vm$: Observable<AssignmentRoleMatrixVm>;

  private readonly destroy$ = new Subject<void>();

  userFilter = '';
  roleFilter = '';

  hoveredRow: ProjectAccess | null = null;
  hoveredColumn: string | null = null;

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new ProjectAccessListLoad(project));
          this.store.dispatch(new AssignmentRoleLoad());
          this.store.dispatch(new ProjectAccessMasksLoad(project));
        }
      });

    this.projectAccessList$ = this.store.select(getProjectAccessList) as Observable<ProjectAccess[]>;
    this.roles$ = this.store.select(getAssignmentRoles) as Observable<AssignmentRole[]>;
    this.masks$ = this.store.select(getProjectAccessMasks) as Observable<ProjectAccessAssignmentMask[]>;

    // Combine all into a single view model so table state changes atomically
    this.vm$ = combineLatest([this.roles$, this.projectAccessList$, this.masks$, this.userFilter$, this.roleFilter$]).pipe(
      map(([roles, projectAccessList, masks, userFilter, roleFilter]) => {
        const safeRoles = roles || [];
        const safeList = this.filterByUser(projectAccessList || [], userFilter || '');
        const filteredByRole = this.filterByRole(safeList, masks || [], safeRoles, roleFilter || '');
        const displayedColumns = ['username', ...safeRoles.map((role) => this.roleToColumn(role))];
        return {
          roles: safeRoles,
          projectAccessList: filteredByRole,
          masks: masks || [],
          displayedColumns,
        } as AssignmentRoleMatrixVm;
      }),
    );
  }

  onUserFilterInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.onUserFilterChange(value);
  }

  onUserFilterChange(value: string): void {
    this.userFilter = value || '';
    this.userFilter$.next(this.userFilter);
  }

  onRoleFilterChange(value: string): void {
    this.roleFilter = value || '';
    this.roleFilter$.next(this.roleFilter);
  }

  private filterByUser(accessList: ProjectAccess[], filter: string): ProjectAccess[] {
    const list = accessList || [];
    if (!filter) {
      return list;
    }
    const lower = filter.toLowerCase();
    return list.filter((pa) => pa.user && pa.user.userName && pa.user.userName.toLowerCase().includes(lower));
  }

  private filterByRole(
    accessList: ProjectAccess[],
    _masks: ProjectAccessAssignmentMask[],
    _roles: AssignmentRole[],
    roleFilter: string,
  ): ProjectAccess[] {
    // Filter by access role (ProjectAccess.role), not assignment role
    const list = accessList || [];
    const trimmed = (roleFilter || '').trim();
    if (!trimmed) {
      return list;
    }
    return list.filter((pa) => pa.role === (ProjectAccessRoles as any)[trimmed] || pa.role === trimmed);
  }

  private getMaskForProjectAccess(masks: ProjectAccessAssignmentMask[] | null | undefined, projectAccessId: number): number {
    if (!masks) {
      return 0;
    }
    const entry = masks.find((m) => m.projectAccessId === projectAccessId);
    return entry ? entry.mask : 0;
  }

  // Helper: check if a given bit (role ordinal) is set in the mask
  private isChecked(mask: number, ordinal: number): boolean {
    return (mask & (1 << ordinal)) !== 0;
  }

  // Helper: toggle a given bit (role ordinal) in the mask
  private toggleBit(mask: number, ordinal: number): number {
    return mask ^ (1 << ordinal);
  }

  private isRoleInMask(mask: number, role: AssignmentRole): boolean {
    if (role == null || (role as any).ordinal == null) {
      return false;
    }
    const ordinal = (role as any).ordinal as number;
    return this.isChecked(mask, ordinal);
  }

  // Check if a role is assigned for a given row, using the full masks array
  hasAssignmentRole(projectAccess: ProjectAccess, role: AssignmentRole, masks: ProjectAccessAssignmentMask[] | null | undefined): boolean {
    const mask = this.getMaskForProjectAccess(masks, projectAccess.id);
    return this.isRoleInMask(mask, role);
  }

  // Toggle a role for a given row, using the full masks array
  toggleAssignmentRole(projectAccess: ProjectAccess, role: AssignmentRole, masks: ProjectAccessAssignmentMask[] | null | undefined): void {
    const currentMask = this.getMaskForProjectAccess(masks, projectAccess.id);
    if (role == null || (role as any).ordinal == null) {
      return;
    }
    const ordinal = (role as any).ordinal as number;
    const newMask = this.toggleBit(currentMask, ordinal);
    this.store.dispatch(new UpdateProjectAccessMask(projectAccess.id, newMask));
  }

  getRoleIcon(projectAccess: ProjectAccess, role: AssignmentRole, masks: ProjectAccessAssignmentMask[] | null | undefined): string {
    return this.hasAssignmentRole(projectAccess, role, masks) ? 'check_circle' : 'radio_button_unchecked';
  }

  roleToColumn(role: AssignmentRole): string {
    // Column ID used by matColumnDef and displayedColumns
    return `role-${role.id}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
